import type { Investor, VoyagerData, VoyagerEvent } from "./types";

export const money = (value = 0) =>
  new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 0,
  }).format(value);

export const shortDate = (value: string) =>
  new Intl.DateTimeFormat("ru-RU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(value));

export const monthKey = (value: string) => value.slice(0, 7);
export const currentMonth = () => new Date().toISOString().slice(0, 7);

export function vehicleFinances(
  events: VoyagerEvent[],
  vehicleId: string,
  month = currentMonth(),
) {
  const list = events.filter(
    (e) => e.vehicleId === vehicleId && monthKey(e.date) === month,
  );
  const income = list
    .filter((e) => e.type === "income")
    .reduce((sum, e) => sum + (e.amount || 0), 0);
  const expense = list
    .filter(
      (e) =>
        ["expense", "repair", "maintenance"].includes(e.type) &&
        e.expenseScope !== "investor_personal",
    )
    .reduce((sum, e) => sum + (e.amount || 0), 0);
  return { income, expense, profit: income - expense };
}

export function investorFinances(
  data: VoyagerData,
  investor: Investor,
  month?: string,
) {
  const vehicleIds = new Set(
    data.vehicles
      .filter((vehicle) => vehicle.investorId === investor.id)
      .map((vehicle) => vehicle.id),
  );
  const events = data.events.filter(
    (event) =>
      (event.investorId === investor.id || vehicleIds.has(event.vehicleId)) &&
      (!month || monthKey(event.date) === month),
  );
  const income = events
    .filter((event) => event.type === "income")
    .reduce((sum, event) => sum + (event.amount || 0), 0);
  const operatingExpenses = events
    .filter(
      (event) =>
        ["expense", "repair", "maintenance"].includes(event.type) &&
        event.expenseScope !== "investor_personal",
    )
    .reduce((sum, event) => sum + (event.amount || 0), 0);
  const personalExpenses =
    events
      .filter(
        (event) =>
          event.type === "expense" &&
          event.expenseScope === "investor_personal",
      )
      .reduce((sum, event) => sum + (event.amount || 0), 0) +
    (month ? 0 : investor.personalExpenses || 0);
  const profit = income - operatingExpenses;
  const base = investor.commissionBase === "revenue" ? income : profit;
  const accrued = Math.max(0, (base * investor.commissionPercent) / 100);
  const payments = data.investorPayments
    .filter(
      (payment) =>
        payment.investorId === investor.id &&
        (!month || (payment.periodMonth || monthKey(payment.date)) === month),
    )
    .reduce((sum, payment) => sum + payment.amount, 0);
  const payable = accrued - personalExpenses - payments;
  const companyShare = profit - accrued;
  return {
    income,
    operatingExpenses,
    personalExpenses,
    profit,
    accrued,
    payments,
    payable,
    companyShare,
  };
}

export function investorTotals(data: VoyagerData, month?: string) {
  return data.investors.reduce(
    (result, investor) => {
      const finance = investorFinances(data, investor, month);
      result.accrued += finance.accrued;
      result.personalExpenses += finance.personalExpenses;
      result.payments += finance.payments;
      result.payable += finance.payable;
      result.companyShare += finance.companyShare;
      return result;
    },
    {
      accrued: 0,
      personalExpenses: 0,
      payments: 0,
      payable: 0,
      companyShare: 0,
    },
  );
}

export function totals(data: VoyagerData) {
  return data.vehicles.reduce(
    (result, vehicle) => {
      const f = vehicleFinances(data.events, vehicle.id);
      result.income += f.income;
      result.expense += f.expense;
      result.profit += f.profit;
      return result;
    },
    { income: 0, expense: 0, profit: 0 },
  );
}

export const uid = () => crypto.randomUUID();
